import React, { useCallback, useEffect, useId, useRef, useState } from "react";

import {
  CROP_MODE_FREE,
  CROP_MODE_FULL,
  INITIAL_ZOOM,
  MAX_ZOOM,
  MIN_ZOOM,
  clampOffset,
  computeEffectiveScale,
  createInitialCropState,
  freeCropToViewportRect,
  nudgeAmountForKeyboard,
  nudgeCrop,
  resizeFreeCrop,
  resolveNearestCropHandle,
  withCropMode,
  withRotation,
  withZoom,
} from "./propertyImageModel";
import {
  PropertyImageExportError,
  createPropertyImageFile,
  EXPORT_DECODE_ERROR,
  EXPORT_EXPORT_ERROR,
} from "./createPropertyImageFile";

const DEFAULT_MAX_VIEWPORT_WIDTH = 360;
const FREE_VIEWPORT_ASPECT = "4 / 3";

function computeViewportDimensions(naturalWidth, naturalHeight, maxWidth, freeMode) {
  if (freeMode) {
    const width = Math.max(120, maxWidth);
    const height = Math.round((width * 3) / 4);
    return { width, height: Math.max(120, height) };
  }
  if (!(naturalWidth > 0) || !(naturalHeight > 0)) {
    return { width: maxWidth, height: maxWidth };
  }
  const aspect = naturalWidth / naturalHeight;
  let width = maxWidth;
  let height = Math.round(width / aspect);
  const maxHeight = 280;
  if (height > maxHeight) {
    height = maxHeight;
    width = Math.round(height * aspect);
  }
  return {
    width: Math.max(120, width),
    height: Math.max(120, height),
  };
}

/**
 * Optional freeform Property photo/plan editor.
 */
function PropertyImageEditor({
  sourceFile,
  kind = "image",
  title = "Редагувати зображення",
  onApply,
  onCancel,
  disabled = false,
  embedded = false,
  onProcessingChange,
}) {
  const titleId = useId();
  const instructionsId = useId();
  const zoomLabelId = useId();
  const viewportRef = useRef(null);
  const objectUrlRef = useRef(null);
  const activePointerIdRef = useRef(null);
  const dragOriginRef = useRef(null);
  const mountedRef = useRef(true);
  const applyRunIdRef = useRef(0);
  const sourceFileRef = useRef(sourceFile);
  sourceFileRef.current = sourceFile;

  const [objectUrl, setObjectUrl] = useState(null);
  const [naturalSize, setNaturalSize] = useState(null);
  const [viewportSize, setViewportSize] = useState({
    width: DEFAULT_MAX_VIEWPORT_WIDTH,
    height: DEFAULT_MAX_VIEWPORT_WIDTH,
  });
  const [cropState, setCropState] = useState(null);
  const [decodeError, setDecodeError] = useState(null);
  const [exportError, setExportError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const invalidateApplyRun = useCallback(() => {
    applyRunIdRef.current += 1;
  }, []);

  const revokeCurrentUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      invalidateApplyRun();
    };
  }, [invalidateApplyRun]);

  const freeMode = cropState?.mode === CROP_MODE_FREE;

  useEffect(() => {
    const el = viewportRef.current;
    if (!el || typeof ResizeObserver === "undefined" || !naturalSize) {
      return undefined;
    }
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const measured = Math.floor(entry.contentRect.width);
      if (measured > 0 && naturalSize) {
        const dims = computeViewportDimensions(
          naturalSize.width,
          naturalSize.height,
          measured,
          freeMode,
        );
        setViewportSize(dims);
      }
    });
    ro.observe(el);
    const initial = Math.floor(el.getBoundingClientRect().width);
    if (initial > 0 && naturalSize) {
      setViewportSize(
        computeViewportDimensions(
          naturalSize.width,
          naturalSize.height,
          initial,
          freeMode,
        ),
      );
    }
    return () => ro.disconnect();
  }, [objectUrl, naturalSize, freeMode]);

  useEffect(() => {
    invalidateApplyRun();
    setNaturalSize(null);
    setCropState(null);
    setDecodeError(null);
    setExportError(null);
    setProcessing(false);

    if (!sourceFile) {
      revokeCurrentUrl();
      setObjectUrl(null);
      return undefined;
    }

    const url = URL.createObjectURL(sourceFile);
    objectUrlRef.current = url;
    setObjectUrl(url);

    return () => {
      URL.revokeObjectURL(url);
      if (objectUrlRef.current === url) {
        objectUrlRef.current = null;
      }
    };
  }, [sourceFile, revokeCurrentUrl, invalidateApplyRun]);

  const handlePreviewLoad = (event) => {
    const img = event.currentTarget;
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!(w > 0) || !(h > 0)) {
      setDecodeError(EXPORT_DECODE_ERROR);
      setNaturalSize(null);
      return;
    }
    setDecodeError(null);
    setNaturalSize({ width: w, height: h });
    setViewportSize(
      computeViewportDimensions(w, h, DEFAULT_MAX_VIEWPORT_WIDTH, false),
    );
  };

  const handlePreviewError = () => {
    setDecodeError(EXPORT_DECODE_ERROR);
    setNaturalSize(null);
  };

  useEffect(() => {
    if (!naturalSize || !(viewportSize.width > 0) || !(viewportSize.height > 0)) {
      return;
    }
    setCropState((prev) => {
      const sameNatural =
        prev
        && (prev.naturalWidth || prev.sourceWidth) === naturalSize.width
        && (prev.naturalHeight || prev.sourceHeight) === naturalSize.height;
      if (sameNatural) {
        const next = {
          ...prev,
          viewportWidth: viewportSize.width,
          viewportHeight: viewportSize.height,
        };
        if (next.mode === CROP_MODE_FREE) {
          return next;
        }
        const clamped = clampOffset(
          next.offsetX,
          next.offsetY,
          next.sourceWidth,
          next.sourceHeight,
          viewportSize.width,
          viewportSize.height,
          next.zoom,
        );
        return { ...next, ...clamped };
      }
      return createInitialCropState(
        naturalSize.width,
        naturalSize.height,
        viewportSize.width,
        viewportSize.height,
      );
    });
  }, [naturalSize, viewportSize]);

  const busy = processing || disabled;
  const surfaceError = decodeError || exportError;

  useEffect(() => {
    if (typeof onProcessingChange !== "function") {
      return undefined;
    }
    onProcessingChange(processing);
    return () => {
      onProcessingChange(false);
    };
  }, [processing, onProcessingChange]);

  const cropStateRef = useRef(cropState);
  const busyRef = useRef(busy);
  const decodeErrorRef = useRef(decodeError);
  cropStateRef.current = cropState;
  busyRef.current = busy;
  decodeErrorRef.current = decodeError;

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;

    const onPointerDown = (event) => {
      if (busyRef.current || !cropStateRef.current || decodeErrorRef.current) {
        return;
      }
      if (activePointerIdRef.current != null) return;
      if (cropStateRef.current.mode !== CROP_MODE_FREE) {
        event.preventDefault();
        el.setPointerCapture?.(event.pointerId);
        activePointerIdRef.current = event.pointerId;
        const state = cropStateRef.current;
        dragOriginRef.current = {
          kind: "pan",
          x: event.clientX,
          y: event.clientY,
          offsetX: state.offsetX,
          offsetY: state.offsetY,
        };
        return;
      }
      const vpRect = freeCropToViewportRect(cropStateRef.current);
      if (!vpRect) return;
      // Ownership is coordinate-resolved, not DOM paint order — opposite 44px
      // targets may overlap on small crops; nearest corner wins.
      const handle = resolveNearestCropHandle(
        event.clientX,
        event.clientY,
        el.getBoundingClientRect(),
        vpRect,
      );
      event.preventDefault();
      el.setPointerCapture?.(event.pointerId);
      activePointerIdRef.current = event.pointerId;
      dragOriginRef.current = {
        kind: handle ? "resize" : "move-free",
        handle: handle || null,
        x: event.clientX,
        y: event.clientY,
        cropX: cropStateRef.current.cropX,
        cropY: cropStateRef.current.cropY,
        cropWidth: cropStateRef.current.cropWidth,
        cropHeight: cropStateRef.current.cropHeight,
        scale: vpRect.scale,
      };
    };

    const onPointerMove = (event) => {
      if (activePointerIdRef.current !== event.pointerId) return;
      const origin = dragOriginRef.current;
      const state = cropStateRef.current;
      if (!origin || !state) return;

      if (origin.kind === "pan") {
        const dx = event.clientX - origin.x;
        const dy = event.clientY - origin.y;
        const clamped = clampOffset(
          origin.offsetX + dx,
          origin.offsetY + dy,
          state.sourceWidth,
          state.sourceHeight,
          state.viewportWidth,
          state.viewportHeight,
          state.zoom,
        );
        setCropState((prev) => (prev ? { ...prev, ...clamped } : prev));
        return;
      }

      const scale = origin.scale || 1;
      const dx = (event.clientX - origin.x) / scale;
      const dy = (event.clientY - origin.y) / scale;

      if (origin.kind === "move-free") {
        setCropState((prev) =>
          nudgeCrop(prev, dx, dy),
        );
        dragOriginRef.current = {
          ...origin,
          x: event.clientX,
          y: event.clientY,
        };
        return;
      }

      if (origin.kind === "resize") {
        setCropState((prev) =>
          resizeFreeCrop(prev, dx, dy, origin.handle || "se"),
        );
        dragOriginRef.current = {
          ...origin,
          x: event.clientX,
          y: event.clientY,
        };
      }
    };

    const endPointerDrag = (event) => {
      if (activePointerIdRef.current !== event.pointerId) return;
      try {
        el.releasePointerCapture?.(event.pointerId);
      } catch (_err) {
        // ignore
      }
      activePointerIdRef.current = null;
      dragOriginRef.current = null;
    };

    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", endPointerDrag);
    el.addEventListener("pointercancel", endPointerDrag);

    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", endPointerDrag);
      el.removeEventListener("pointercancel", endPointerDrag);
      activePointerIdRef.current = null;
      dragOriginRef.current = null;
    };
  }, [objectUrl, freeMode]);

  const handleViewportKeyDown = (event) => {
    if (event.key === "Escape" && !busyRef.current) {
      if (embedded) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      invalidateApplyRun();
      setProcessing(false);
      setExportError(null);
      onCancel?.();
      return;
    }
    if (busyRef.current || !cropStateRef.current || decodeErrorRef.current) {
      return;
    }
    const map = {
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
      ArrowUp: [0, -1],
      ArrowDown: [0, 1],
    };
    const delta = map[event.key];
    if (!delta) return;
    event.preventDefault();
    event.stopPropagation();
    const amount = nudgeAmountForKeyboard(event.shiftKey);
    setCropState((prev) =>
      nudgeCrop(prev, delta[0] * amount, delta[1] * amount),
    );
  };

  const HANDLE_LABELS = {
    nw: "Змінити розмір кадру: верхній лівий кут",
    ne: "Змінити розмір кадру: верхній правий кут",
    sw: "Змінити розмір кадру: нижній лівий кут",
    se: "Змінити розмір кадру: нижній правий кут",
  };

  const handleResizeKeyDown = (handle) => (event) => {
    if (busyRef.current || !cropStateRef.current || decodeErrorRef.current) {
      return;
    }
    if (event.key === "Escape") {
      if (embedded) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      invalidateApplyRun();
      setProcessing(false);
      setExportError(null);
      onCancel?.();
      return;
    }
    const map = {
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
      ArrowUp: [0, -1],
      ArrowDown: [0, 1],
    };
    const delta = map[event.key];
    if (!delta) return;
    event.preventDefault();
    event.stopPropagation();
    const amount = nudgeAmountForKeyboard(event.shiftKey);
    setCropState((prev) =>
      resizeFreeCrop(prev, delta[0] * amount, delta[1] * amount, handle),
    );
  };

  const handleZoomChange = (event) => {
    if (busy || !cropState || cropState.mode === CROP_MODE_FREE) return;
    const next = Number(event.target.value);
    setCropState((prev) => withZoom(prev, next));
  };

  const handleReset = () => {
    if (busy || !naturalSize) return;
    setExportError(null);
    setCropState(
      createInitialCropState(
        naturalSize.width,
        naturalSize.height,
        viewportSize.width,
        viewportSize.height,
      ),
    );
  };

  const handleRotate = (delta) => {
    if (busy || !cropState || decodeError) return;
    setExportError(null);
    setCropState((prev) => withRotation(prev, delta));
  };

  const handleModeToggle = (mode) => {
    if (busy || !cropState) return;
    setExportError(null);
    setCropState((prev) => withCropMode(prev, mode));
  };

  const canCommitApplyRun = (runId, sourceFileAtStart) =>
    mountedRef.current
    && applyRunIdRef.current === runId
    && sourceFileRef.current === sourceFileAtStart;

  const handleCancel = () => {
    invalidateApplyRun();
    setProcessing(false);
    setExportError(null);
    onCancel?.();
  };

  const handleApply = async () => {
    if (busy || !cropState || !sourceFile || decodeError) return;
    const runId = ++applyRunIdRef.current;
    const sourceFileAtStart = sourceFile;
    setProcessing(true);
    setExportError(null);
    try {
      const edited = await createPropertyImageFile(
        sourceFileAtStart,
        cropState,
        kind,
      );
      if (!canCommitApplyRun(runId, sourceFileAtStart)) {
        return;
      }
      onApply?.(edited);
    } catch (err) {
      if (!canCommitApplyRun(runId, sourceFileAtStart)) {
        return;
      }
      const message =
        err instanceof PropertyImageExportError
          ? err.userMessage
          : EXPORT_EXPORT_ERROR;
      setExportError(message);
    } finally {
      if (mountedRef.current && applyRunIdRef.current === runId) {
        setProcessing(false);
      }
    }
  };

  const effectiveScale =
    cropState
    && cropState.mode !== CROP_MODE_FREE
    && computeEffectiveScale(
      cropState.sourceWidth,
      cropState.sourceHeight,
      cropState.viewportWidth,
      cropState.viewportHeight,
      cropState.zoom,
    );

  const rotation = cropState?.rotation || 0;

  const imageStyle =
    cropState && naturalSize
      ? cropState.mode === CROP_MODE_FREE
        ? (() => {
            const fit = computeEffectiveScale(
              cropState.sourceWidth,
              cropState.sourceHeight,
              cropState.viewportWidth,
              cropState.viewportHeight,
              INITIAL_ZOOM,
            );
            return {
              width: `${naturalSize.width * fit}px`,
              height: `${naturalSize.height * fit}px`,
              transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
            };
          })()
        : effectiveScale
          ? {
              width: `${naturalSize.width * effectiveScale}px`,
              height: `${naturalSize.height * effectiveScale}px`,
              transform: `translate(-50%, -50%) translate(${cropState.offsetX}px, ${cropState.offsetY}px) rotate(${rotation}deg)`,
            }
          : {
              width: "100%",
              height: "100%",
              objectFit: "contain",
              transform: "translate(-50%, -50%)",
            }
      : {
          width: "100%",
          height: "100%",
          objectFit: "contain",
          transform: "translate(-50%, -50%)",
        };

  const viewportAspect = freeMode
    ? FREE_VIEWPORT_ASPECT
    : naturalSize && naturalSize.width > 0 && naturalSize.height > 0
      ? `${naturalSize.width} / ${naturalSize.height}`
      : "4 / 3";

  const freeOverlay = freeMode && cropState
    ? freeCropToViewportRect(cropState)
    : null;

  const editorRootProps = embedded
    ? {
        className: "avatar-crop-editor",
        "data-property-image-editor": "editor",
        "data-property-image-editor-embedded": "true",
      }
    : {
        className: "avatar-crop-editor",
        "data-property-image-editor": "editor",
        role: "dialog",
        "aria-modal": "true",
        "aria-labelledby": titleId,
      };

  return (
    <div {...editorRootProps}>
      {embedded ? null : (
        <h3 id={titleId} className="avatar-crop-editor__title">
          {title}
        </h3>
      )}

      <div
        ref={viewportRef}
        className="avatar-crop-editor__viewport"
        tabIndex={0}
        role="group"
        aria-label="Область редагування зображення"
        aria-describedby={instructionsId}
        data-property-image-editor="viewport"
        data-property-image-editor-crop-surface="true"
        onKeyDown={handleViewportKeyDown}
        style={{ aspectRatio: viewportAspect, maxWidth: "100%", position: "relative" }}
      >
        {objectUrl ? (
          <img
            src={objectUrl}
            alt=""
            draggable={false}
            className="avatar-crop-editor__image"
            data-property-image-editor="preview"
            onLoad={handlePreviewLoad}
            onError={handlePreviewError}
            style={imageStyle}
          />
        ) : null}
        {freeOverlay ? (
          <div
            className="avatar-crop-editor__crop-overlay"
            data-property-image-editor="crop-surface"
            style={{
              position: "absolute",
              left: `${freeOverlay.left}px`,
              top: `${freeOverlay.top}px`,
              width: `${freeOverlay.width}px`,
              height: `${freeOverlay.height}px`,
              boxSizing: "border-box",
              border: "2px solid #fff",
              boxShadow: "0 0 0 9999px rgba(0,0,0,0.45)",
              cursor: "move",
              minWidth: 28,
              minHeight: 28,
            }}
          >
            {["nw", "ne", "sw", "se"].map((handle) => (
              <button
                key={handle}
                type="button"
                data-crop-handle={handle}
                data-property-image-editor="crop-handle"
                aria-label={HANDLE_LABELS[handle]}
                disabled={busy || Boolean(decodeError)}
                onKeyDown={handleResizeKeyDown(handle)}
                style={{
                  position: "absolute",
                  width: 44,
                  height: 44,
                  padding: 0,
                  margin: 0,
                  background: "transparent",
                  border: "none",
                  borderRadius: 0,
                  cursor: `${handle}-resize`,
                  outlineOffset: 2,
                  touchAction: "none",
                  // Pointer geometry is resolved on the viewport; buttons remain
                  // keyboard-focusable markers without competing hit ownership.
                  pointerEvents: "none",
                  display: "block",
                  ...(handle.includes("n") ? { top: -22 } : { bottom: -22 }),
                  ...(handle.includes("w") ? { left: -22 } : { right: -22 }),
                }}
              >
                <span
                  aria-hidden="true"
                  data-property-image-editor="crop-handle-marker"
                  style={{
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    width: 16,
                    height: 16,
                    transform: "translate(-50%, -50%)",
                    background: "#fff",
                    border: "1px solid #333",
                    pointerEvents: "none",
                    boxSizing: "border-box",
                  }}
                />
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <p id={instructionsId} className="avatar-crop-editor__instructions">
        Перетягніть для зміщення, оберіть вільний кадр або обертайте. За
        замовчуванням зберігається повний кадр.
      </p>

      <div className="avatar-crop-editor__rotate">
        <button
          type="button"
          className="avatar-crop-editor__btn avatar-crop-editor__btn--secondary"
          disabled={busy || Boolean(decodeError)}
          aria-pressed={cropState?.mode === CROP_MODE_FULL}
          onClick={() => handleModeToggle(CROP_MODE_FULL)}
        >
          Повний кадр
        </button>
        <button
          type="button"
          className="avatar-crop-editor__btn avatar-crop-editor__btn--secondary"
          disabled={busy || Boolean(decodeError)}
          aria-pressed={cropState?.mode === CROP_MODE_FREE}
          onClick={() => handleModeToggle(CROP_MODE_FREE)}
        >
          Вільний кадр
        </button>
        <button
          type="button"
          className="avatar-crop-editor__btn avatar-crop-editor__btn--secondary"
          disabled={busy || Boolean(decodeError)}
          onClick={() => handleRotate(-90)}
          aria-label="Повернути ліворуч на 90 градусів"
        >
          ↺ 90°
        </button>
        <button
          type="button"
          className="avatar-crop-editor__btn avatar-crop-editor__btn--secondary"
          disabled={busy || Boolean(decodeError)}
          onClick={() => handleRotate(90)}
          aria-label="Повернути праворуч на 90 градусів"
        >
          ↻ 90°
        </button>
        <button
          type="button"
          className="avatar-crop-editor__btn avatar-crop-editor__btn--secondary"
          disabled={busy || !naturalSize}
          onClick={handleReset}
        >
          Скинути кадр
        </button>
      </div>

      <div className="avatar-crop-editor__zoom">
        <label htmlFor={zoomLabelId}>Масштаб</label>
        <input
          id={zoomLabelId}
          type="range"
          min={MIN_ZOOM}
          max={MAX_ZOOM}
          step={0.01}
          value={cropState?.zoom ?? INITIAL_ZOOM}
          disabled={
            busy
            || !cropState
            || Boolean(decodeError)
            || cropState?.mode === CROP_MODE_FREE
          }
          onChange={handleZoomChange}
          aria-valuemin={MIN_ZOOM}
          aria-valuemax={MAX_ZOOM}
        />
      </div>

      {surfaceError ? (
        <p className="avatar-crop-editor__error" role="alert">
          {surfaceError}
        </p>
      ) : null}
      {processing ? (
        <p className="avatar-crop-editor__status" role="status">
          Експорт…
        </p>
      ) : null}

      <div className="avatar-crop-editor__actions">
        <button
          type="button"
          className="avatar-crop-editor__btn avatar-crop-editor__btn--secondary"
          disabled={busy}
          onClick={handleCancel}
        >
          Скасувати
        </button>
        <button
          type="button"
          className="avatar-crop-editor__btn avatar-crop-editor__btn--primary"
          disabled={busy || !cropState || Boolean(decodeError)}
          onClick={handleApply}
        >
          Застосувати
        </button>
      </div>
    </div>
  );
}

export default PropertyImageEditor;
